/**
 * playerDots — pure presentation logic for the collapsed player dot strip
 * that lives in the in-game top bar (see components/game/PlayerDotsStrip).
 *
 * Kept framework-free so the who's-live / whose-turn / eliminated rules can be
 * unit-tested without mounting React Native. The renderer maps the returned
 * flags to colour, halo, ring, and chevron.
 */

export interface PlayerDotState {
  /**
   * True → draw the green "online" glow. Only live HUMANS glow: bots are
   * never shown as online (per product decision) even though the game treats
   * them as always-present for turn-advancement purposes.
   */
  glow: boolean
  /** True → this player is the current turn actor (yellow ring + chevron). */
  onTurn: boolean
  /** True → dim the dot (player is out of the game). */
  eliminated: boolean
}

const DOT_GAP = 4
/**
 * Team games: the diamond and hexagon overshoot the dot's box by about a
 * pixel (optical size), so their tips get a little more room to keep the
 * spacing even.
 */
export const TEAM_DOT_GAP = DOT_GAP + 2
const DOT_MIN_SIZE = 10
const DOT_MAX_SIZE = 20
const LARGE_ROSTER_MAX_SIZE = 16
const DOT_OVERHEAD = 4
const DOT_GLYPH_SCALE = 0.52
const PILL_HORIZONTAL_INSET = 18 // px-2 on both sides + 1px borders
const PILL_RIGHT_MARGIN = 6

/**
 * Calculate the inner diameter used by the compact top-bar dots.
 *
 * Keeping this arithmetic outside the renderer makes the fit contract
 * explicit: the resulting pill width includes the ring, gap, padding, and
 * the right-side breathing room used by the header.
 */
export function playerDotSize(slotWidth: number, playerCount: number, gap: number = DOT_GAP): number {
  if (playerCount <= 0) return DOT_MAX_SIZE

  const maxSize = playerCount >= 7 ? LARGE_ROSTER_MAX_SIZE : DOT_MAX_SIZE
  if (slotWidth <= 0) return maxSize

  return Math.max(
    DOT_MIN_SIZE,
    Math.min(
      maxSize,
      Math.floor(
        (slotWidth - PILL_HORIZONTAL_INSET - PILL_RIGHT_MARGIN - gap * (playerCount - 1)) /
          playerCount,
      ) - DOT_OVERHEAD,
    ),
  )
}

/** Return the exact width of the centered, bordered player pill. */
export function playerDotsPillWidth(
  dotSize: number,
  playerCount: number,
  onTurnCount: number,
  gap: number = DOT_GAP,
): number {
  const ringWidth = playerCount > 0 ? dotSize + 2 : 0
  const onTurnRingExtra = Math.min(playerCount, Math.max(0, onTurnCount)) * 2
  return (
    playerCount * ringWidth +
    onTurnRingExtra +
    Math.max(0, playerCount - 1) * gap +
    PILL_HORIZONTAL_INSET
  )
}

/** Keep the player initial proportional when the available slot requires it. */
export function playerDotFontSize(dotSize: number): number {
  return dotSize * DOT_GLYPH_SCALE
}

/**
 * Derive the dot presentation flags for a single player. The dot's fill colour
 * is resolved by the renderer from the slot (kept out of here so the helper
 * stays free of the `@warcarp/ui` barrel and unit-testable).
 *
 * `presence` is the set of userIds with at least one open socket. Bots are
 * excluded from the glow regardless of presence; an eliminated player never
 * reads as on-turn even if the engine pointer briefly lands on them.
 */
export function playerDotState(args: {
  isBot: boolean
  userId: string
  isTurn: boolean
  eliminated: boolean
  presence: Set<string>
}): PlayerDotState {
  return {
    glow: !args.isBot && !args.eliminated && args.presence.has(args.userId),
    onTurn: args.isTurn && !args.eliminated,
    eliminated: args.eliminated,
  }
}

